import { InformationCircleIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import type {
  PartnershipBusinessCandidateDto,
  PartnershipBusinessDetectResultDto,
  PartnershipSettingsDto,
} from "~/ui-rtk/api/types/partnerships.types"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import AgencyCredentialDialog from "./AgencyCredentialDialog"
import {
  BUSINESS_CANDIDATE_SOURCE_LABEL,
  BUSINESS_ID_HELP,
  BUSINESS_ID_LABEL,
  BUSINESS_ID_SOURCE_LABEL,
  BUSINESS_ID_WHERE_TO_FIND,
  BUSINESS_MANAGER_PANEL_ID,
  businessOutcomeChip,
  businessOutcomeFix,
  formatBrandCredentialLine,
  formatBusinessLabel,
  formatDiscoveryCredentialLine,
  type BusinessFixCredentialContext,
  type CredentialLineTone,
} from "./partnership-copy"
import { StatusChip } from "./PartnershipsTable"

const CREDENTIAL_LINE_CLS: Record<CredentialLineTone, string> = {
  ok: "text-system-green-300",
  warning: "text-system-yellow-200",
  neutral: "text-system-gray-400",
}

/**
 * The Business Manager panel (design §1.1): explains which business id
 * content discovery runs through, shows the effective value with its name and
 * source, who the connected Meta user is and which brand account discovery
 * looks up, and — for super-admins — lets them Detect the businesses the
 * connected user can reach, pick one, or type an id by hand. Regular users see
 * the same facts read-only.
 */

export interface SaveSettingsResult {
  ok: boolean
  /** The API's probe of the saved id; null when nothing could be probed. */
  verification: PartnershipBusinessCandidateDto | null
}

export interface PartnershipSettingsInlineProps {
  settings: PartnershipSettingsDto
  /** Only super-admins may write (the PUT / detect routes are AUTH_SUPER_ADMIN). */
  canEdit: boolean
  onSave: (businessId: string | null) => Promise<SaveSettingsResult>
  onDetect: () => Promise<PartnershipBusinessDetectResultDto | null>
}

interface CandidateRowProps {
  candidate: PartnershipBusinessCandidateDto
  recommended: boolean
  /** Already the effective id (override or default) — nothing to switch to. */
  inUse: boolean
  connectedUserName: string | null
  credential: BusinessFixCredentialContext
  busy: boolean
  onUse: (businessId: string) => void
}
function CandidateRow({
  candidate,
  recommended,
  inUse,
  connectedUserName,
  credential,
  busy,
  onUse,
}: CandidateRowProps) {
  const chip = businessOutcomeChip(candidate)
  const fix = businessOutcomeFix(candidate, connectedUserName, credential)
  return (
    <li
      data-testid={`business-candidate-${candidate.businessId}`}
      className="flex flex-col gap-1 rounded-md border border-basic-blue bg-basic-black px-system-sm py-system-2xs"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-white">
          {formatBusinessLabel(candidate.businessId, candidate.name)}
        </span>
        <StatusChip tone={chip.tone} title={candidate.detail ?? undefined}>
          {chip.label}
        </StatusChip>
        {recommended && (
          <StatusChip
            tone="blue"
            title="First business that works, in precedence order"
          >
            Recommended
          </StatusChip>
        )}
        <span className="text-system-gray-400">
          via{" "}
          {candidate.sources
            .map(s => BUSINESS_CANDIDATE_SOURCE_LABEL[s])
            .join(", ")}
        </span>
        <span className="ml-auto">
          {inUse ? (
            <span className="text-system-gray-300">In use</span>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onUse(candidate.businessId)}
              aria-label={`Use ${candidate.businessId}`}
              className="btn btn-primary btn-xs"
            >
              Use
            </button>
          )}
        </span>
      </div>
      {fix && <p className="text-system-gray-300">{fix}</p>}
    </li>
  )
}

export default function PartnershipSettingsInline({
  settings,
  canEdit,
  onSave,
  onDetect,
}: PartnershipSettingsInlineProps) {
  const [value, setValue] = useState(settings.companyBusinessId ?? "")
  const [saving, setSaving] = useState(false)
  const [detecting, setDetecting] = useState(false)
  const [detected, setDetected] =
    useState<PartnershipBusinessDetectResultDto | null>(null)
  const [verification, setVerification] = useState<{
    businessId: string | null
    candidate: PartnershipBusinessCandidateDto | null
  } | null>(null)

  const [manageOpen, setManageOpen] = useState(false)

  const busy = saving || detecting
  const connectedUserName =
    detected?.connectedUser?.name ?? settings.connectedUserName
  const brandHandle =
    detected?.brandIgUsername ?? settings.brandIgUsername ?? null
  const brandIgUserId = detected?.brandIgUserId ?? settings.brandIgUserId

  // Which credentials the fix-it copy should name: the brand connector's, and
  // the one the probe ran on (a Detect result says which; else the settings).
  const discoveryCredential = detected?.discoveryCredential ?? {
    kind: settings.discoveryCredential.kind,
    name: settings.discoveryCredential.name,
  }
  const credentialContext: BusinessFixCredentialContext = {
    brand: settings.credentialKind,
    discovery: discoveryCredential.kind,
    discoveryName: discoveryCredential.name,
  }
  const discoveryLine = formatDiscoveryCredentialLine(
    settings.discoveryCredential,
    brandHandle,
  )
  let detectedWho: string | null
  switch (discoveryCredential.kind) {
    case "agency":
      detectedWho = discoveryCredential.name
        ? `the agency login ${discoveryCredential.name}`
        : "the agency login"
      break
    case "account_user":
      detectedWho = discoveryCredential.name
        ? `the account user ${discoveryCredential.name}`
        : "the account user"
      break
    case "connector_person":
      detectedWho = connectedUserName ?? "the connected user"
      break
    case "none":
      detectedWho = null
      break
  }

  const save = async (next: string | null) => {
    setSaving(true)
    const result = await onSave(next)
    setSaving(false)
    if (!result.ok) return
    setVerification({ businessId: next, candidate: result.verification })
    if (next === null) setValue("")
    else setValue(next)
  }

  const detect = async () => {
    setDetecting(true)
    const result = await onDetect()
    setDetecting(false)
    if (result) setDetected(result)
  }

  const verificationChip = verification?.candidate
    ? businessOutcomeChip(verification.candidate)
    : null
  const verificationFix = verification?.candidate
    ? businessOutcomeFix(
        verification.candidate,
        connectedUserName,
        credentialContext,
      )
    : null

  const lastIdentity = settings.lastIdentity
  const lastSyncUsedDifferentId =
    lastIdentity !== null &&
    lastIdentity.businessId !== null &&
    lastIdentity.businessId !== settings.businessId

  return (
    <section
      id={BUSINESS_MANAGER_PANEL_ID}
      aria-label={BUSINESS_ID_LABEL}
      data-testid="business-manager-panel"
      className="flex flex-col gap-2 rounded-md border border-basic-blue bg-basic-dark-blue px-system-md py-system-sm text-xs text-gray-300"
    >
      <div className="flex items-center gap-1">
        <span className="font-medium text-white">{BUSINESS_ID_LABEL}</span>
        <span
          role="img"
          aria-label="Where to find the Business Manager id"
          title={BUSINESS_ID_WHERE_TO_FIND}
          className="inline-flex cursor-help text-system-gray-300 hover:text-white"
        >
          <InformationCircleIcon className="h-4 w-4" />
        </span>
      </div>
      <p className="max-w-3xl">{BUSINESS_ID_HELP}</p>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>
          Using:{" "}
          <span className="font-mono text-white">
            {formatBusinessLabel(settings.businessId, settings.businessName)}
          </span>{" "}
          ({BUSINESS_ID_SOURCE_LABEL[settings.businessIdSource]})
        </span>
        <span className="text-system-gray-400">
          {canEdit && connectedUserName
            ? `Connected as ${connectedUserName} · brand `
            : "Brand "}
          {brandHandle
            ? `@${brandHandle}`
            : (brandIgUserId ??
              "Instagram account not picked on the connector")}
        </span>
        {!canEdit && (
          <span className="text-system-gray-400">
            Ask a Marathon admin to change it.
          </span>
        )}
      </div>

      {/* Which credential the brand's reads/writes run on vs. which one content
          discovery runs on (Meta refuses system users there). Super admins
          only — the names are redacted for everyone else anyway. */}
      {canEdit && (
        <div
          data-testid="credential-lines"
          className="flex flex-wrap items-center gap-x-3 gap-y-1"
        >
          <span data-testid="brand-credential-line">
            {formatBrandCredentialLine(
              settings.credentialKind,
              settings.credentialName,
            )}
          </span>
          <span
            data-testid="discovery-credential-line"
            className={cn(CREDENTIAL_LINE_CLS[discoveryLine.tone])}
          >
            {discoveryLine.text}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={() => setManageOpen(true)}
            title="Set, replace or remove the agency login content discovery runs on for brands connected with a system user"
            className="btn btn-outline btn-xs"
          >
            Manage
          </button>
        </div>
      )}
      {manageOpen && (
        <AgencyCredentialDialog
          onClose={() => setManageOpen(false)}
          coversBrand={settings.discoveryCredential.coversBrand}
          brandIgUsername={brandHandle}
        />
      )}

      {lastSyncUsedDifferentId && (
        <p className="text-system-yellow-200">
          The last sync ran discovery through{" "}
          <span className="font-mono">
            {formatBusinessLabel(
              lastIdentity.businessId,
              lastIdentity.businessName,
            )}
          </span>{" "}
          ({BUSINESS_ID_SOURCE_LABEL[lastIdentity.businessIdSource]}) — Refresh
          from Meta to use the current setting.
        </p>
      )}

      {canEdit && (
        <>
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={e => {
              e.preventDefault()
              const trimmed = value.trim()
              if (trimmed) void save(trimmed)
            }}
          >
            <Input
              aria-label="Business Manager id override"
              placeholder="Business Manager id"
              value={value}
              onChange={e => setValue(e.target.value)}
              className="h-8"
              width={200}
            />
            <button
              type="submit"
              disabled={busy || !value.trim()}
              className="btn btn-primary btn-xs"
            >
              Save
            </button>
            {settings.companyBusinessId && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void save(null)}
                title={
                  settings.defaultBusinessId
                    ? `Remove the override and use the default (${settings.defaultBusinessId})`
                    : "Remove the override (no default is configured)"
                }
                className="btn btn-ghost btn-xs"
              >
                Use default
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => void detect()}
              title="Probe every Business Manager the connected Meta user can reach and show which ones work for this brand"
              className="btn btn-outline btn-xs"
            >
              {detecting ? "Detecting…" : "Detect"}
            </button>
          </form>

          {verification && (
            <div
              role="status"
              className="flex flex-col gap-1"
              data-testid="business-verification"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span>
                  {verification.businessId === null
                    ? "Override removed."
                    : "Saved."}
                </span>
                {verificationChip ? (
                  <StatusChip
                    tone={verificationChip.tone}
                    title={verification.candidate?.detail ?? undefined}
                  >
                    {verificationChip.label}
                  </StatusChip>
                ) : (
                  <span className="text-system-gray-400">
                    Not verified — Detect to check it.
                  </span>
                )}
              </div>
              {verificationFix && (
                <p className="text-system-gray-300">{verificationFix}</p>
              )}
            </div>
          )}

          {detected && (
            <div className="flex flex-col gap-1">
              <span className="font-medium text-white">
                {detectedWho
                  ? `Businesses ${detectedWho} can run discovery through`
                  : "No discovery credential — every business was skipped"}
              </span>
              {detected.candidates.length === 0 ? (
                <p>
                  {discoveryCredential.kind === "none" &&
                    "No agency discovery credential is set — add one with Manage above, then Detect again."}
                  {discoveryCredential.kind === "agency" &&
                    "Meta lists no businesses for the agency login. Set the agency discovery credential again with a login that belongs to the brand's Business Manager."}
                  {discoveryCredential.kind === "account_user" &&
                    "Detect checks only the business on this company's Meta access assignment. The assignment names none, or the account user is not a member of it. Set the business in Settings → Company → Meta access, then Detect again."}
                  {discoveryCredential.kind === "connector_person" &&
                    "Meta lists no businesses for the connected user. Reconnect the Instagram Insights connector with a user who belongs to the brand's Business Manager."}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {detected.candidates.map(candidate => (
                    <CandidateRow
                      key={candidate.businessId}
                      candidate={candidate}
                      recommended={
                        candidate.businessId === detected.recommendedBusinessId
                      }
                      inUse={candidate.businessId === settings.businessId}
                      connectedUserName={connectedUserName}
                      credential={credentialContext}
                      busy={busy}
                      onUse={id => void save(id)}
                    />
                  ))}
                </ul>
              )}
              {detected.candidates.length > 0 &&
                detected.recommendedBusinessId === null && (
                  <p className="text-system-yellow-200">
                    None of these businesses can discover this brand&apos;s
                    content today — follow the fix under each one.
                  </p>
                )}
            </div>
          )}
        </>
      )}
    </section>
  )
}
